import { Button } from "@pondigitalsolutions/pon-component-library";
import { useCluster } from "@hooks/useCluster";
import { useTranslation } from "react-i18next";
import { useUserContext } from "@hooks/useUserContext";

interface ClusterFilterProps {
  selectedCluster: string[];
  setSelectedCluster: (cluster: string[]) => void;
}

export const ClusterFilter = ({
  selectedCluster,
  setSelectedCluster,
}: ClusterFilterProps) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { data, isLoading, error } = useCluster({
    content_type: "cluster",
  });

  const commonStyles =
    "lg:h-8 lg:text-base px-2 lg:px-3.5 rounded-lg cursor-pointer";

  // Calculate min-height based on:
  // - Padding: pt-10 pb-9 (40px + 36px = 76px) on small, pt-15 (60px) + pb-9 (36px = 96px) on lg
  // - Button height: default size "md" (~32px) on small, lg:h-8 (32px) on large
  // - Gap between rows: gap-2 (8px) - assume at least one gap for wrapped content
  // Small screens: 76px + 32px + 8px = ~116px ≈ min-h-[116px]
  // Large screens: 96px + 32px + 8px = ~136px ≈ min-h-32 (128px) on lg
  const minHeightClasses = "min-h-[116px] lg:min-h-32";

  // Filter clusters based on leadership - only show leadership clusters to leaders
  const filteredClusters =
    data?.storyData?.filter((cluster) => {
      // If cluster is marked as leadership-only, only show it to leaders
      if (cluster.content?.isLeadership) {
        return user?.isLeader === true;
      }
      // Show all non-leadership clusters to everyone
      return true;
    }) || [];

  return (
    <nav
      aria-label={t("StartPage.newsFilters")}
      className={`flex justify-center bg-secondary-900 pt-8 pb-9 lg:pt-16 dark:bg-secondary-950 ${minHeightClasses}`}
    >
      <div className="flex w-72 flex-wrap justify-center gap-2 md:w-164 lg:w-212 xl:w-262">
        {!isLoading && !error && (
          <>
            <Button
              id="filter-cluster-all"
              size="md"
              variant={selectedCluster.length === 0 ? "primary" : "soft"}
              className={commonStyles}
              onClick={() => setSelectedCluster([])}
              ariaLabel={t("ClusterFilters.filterAllNews")}
            >
              {t("Global.all")}
            </Button>
            {filteredClusters.map((cluster) => (
              <Button
                key={cluster.uuid}
                id={`filter-cluster-${cluster.name}`}
                size="md"
                variant={
                  selectedCluster.includes(cluster.uuid) ? "primary" : "soft"
                }
                className={commonStyles}
                onClick={() => {
                  // If the cluster is already selected, remove it from the selectedCluster array.
                  if (selectedCluster.includes(cluster.uuid)) {
                    setSelectedCluster(
                      selectedCluster.filter((id) => id !== cluster.uuid)
                    );
                  } else {
                    // Update the cluster filter to only show the selected cluster (replace any existing filters).
                    setSelectedCluster([cluster.uuid]);
                  }
                }}
                ariaLabel={t("ClusterFilters.filterNewsByCluster", {
                  cluster: cluster.name,
                })}
              >
                {cluster.name}
              </Button>
            ))}
          </>
        )}
      </div>
    </nav>
  );
};
